/**
 * LAB-107: questions that ask which models (items, products, units ...) in an
 * attached chart or table have given features. QA attached a chart of incubator
 * models and asked which support both SafeCell UV and H₂O₂ decontamination; the
 * reply listed models, and nothing checked them against the chart.
 *
 * Two parts:
 * - guidance added to the reading and answering steps, so the chart is read row
 *   by row and an item is listed only when its row shows every feature asked for;
 * - an independent review of the reply against the chart reading: every item the
 *   reply names must be in the reading, and the row that names it must show each
 *   feature. Items that fail are marked unverified with the reason.
 */

export const CHART_ANALYSIS_BEHAVIOR =
  "A model counts only when it is in the reading of the attached chart or table and the row that names it shows every feature asked for. A model the reading does not name, or whose row does not show a requested feature, is marked unverified instead of accepted."

export const CHART_ANALYSIS_SCENARIOS = [
  "Attach a chart of incubator models against features and ask which models support both SafeCell UV and H₂O₂ decontamination. Each model in the reply is checked against its row in the chart reading, and the reply shows which rows were verified.",
  "The reply names a model that is not in the chart reading. That model is marked unverified: not in the chart.",
  "The reply names a model whose row shows only one of the two features. That model is marked unverified, naming the feature its row does not show.",
  "Ask for a feature that no row shows. The reply says it was not found in the chart and does not infer a model from its name or from outside knowledge.",
]

export type ChartLookup = {
  /** The feature phrases the question asks for, as written. */
  features: string[]
  /** "all" when every feature must be shown (and / both); "any" when the question says "or". */
  mode: "all" | "any"
}

export type ChartItemReview = {
  item: string
  status: "verified" | "unverified"
  reason: string
}

export type ChartReview = {
  grounded: boolean
  summary: string
  items: ChartItemReview[]
  features: string[]
}

const ITEM_NOUN =
  "models?|items?|products?|units?|instruments?|incubators?|devices?|systems?|options?|variants?|versions?|series|types?|configurations?|entries|rows?|ones?"
const HAS_VERB =
  "support(?:s|ing)?|with|have|has|having|offer(?:s|ing)?|include(?:s|ing)?|feature(?:s|ing)?|provide(?:s|ing)?|comes?\\s+with|equipped\\s+with|capable\\s+of|allow(?:s|ing)?|enable(?:s|ing)?"

const LOOKUP_QUESTION = new RegExp(
  `\\b(?:which|what|list|name|identify|find|show)\\b[^.?!]{0,60}?\\b(?:${ITEM_NOUN})\\b[^.?!]{0,60}?\\b(?:${HAS_VERB})\\b\\s*(?:both\\s+|all\\s+of\\s+|either\\s+|the\\s+)?([^?!\\n]+)`,
  "i"
)

const TRAILING_SOURCE =
  /\s+(?:according\s+to|based\s+on|from|in|on|per|using)\s+(?:the\s+|this\s+|that\s+|my\s+)?(?:attached\s+|uploaded\s+)?(?:chart|table|image|picture|file|attachment|document|pdf|screenshot|graph|figure|matrix|comparison|list)\b[^,]*$/i

const FEATURE_SPLIT =
  /\s*(?:,|;|\band\b|&|\bas\s+well\s+as\b|\bplus\b|\bor\b|\/)\s*/i

const GENERIC_FEATURE_WORDS = new Set([
  "decontamination",
  "decontam",
  "sterilisation",
  "sterilization",
  "disinfection",
  "cleaning",
  "system",
  "systems",
  "mode",
  "modes",
  "function",
  "functions",
  "option",
  "options",
  "feature",
  "features",
  "capability",
  "capabilities",
  "support",
  "supported",
  "control",
  "controls",
  "technology",
  "type",
  "types",
  "with",
  "both",
  "the",
  "and",
  "for",
  "built",
  "builtin",
  "integrated",
  "standard",
  "available",
])

const SUBSCRIPTS: Record<string, string> = {
  "₀": "0",
  "₁": "1",
  "₂": "2",
  "₃": "3",
  "₄": "4",
  "₅": "5",
  "₆": "6",
  "₇": "7",
  "₈": "8",
  "₉": "9",
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
}

function plainDigits(value: string) {
  return value.replace(/[₀-₉⁰¹²³⁴-⁹]/g, (digit) => SUBSCRIPTS[digit] || digit)
}

/** Lower case, plain digits, letters and digits only: "H₂O₂ decontam." and "h2o2 decontam" compare equal. */
function compact(value: string) {
  return plainDigits(value)
    .toLowerCase()
    .replace(/[^a-z0-9%]/g, "")
}

/**
 * True when the code appears in the text as whole tokens: "VIOS 160i" matches
 * "VIOS160i" and "VIOS 160i", but "MCO-230AICUV" does not match "MCO-230AICUVH".
 */
export function containsCode(text: string, code: string) {
  const needle = compact(code)
  if (!needle) return false
  const tokens = wordsOf(text)
  for (let start = 0; start < tokens.length; start += 1) {
    let joined = ""
    for (let end = start; end < tokens.length; end += 1) {
      joined += tokens[end]
      if (joined === needle) return true
      if (joined.length >= needle.length) break
    }
  }
  return false
}

function wordsOf(value: string) {
  return plainDigits(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** The words of a feature phrase that identify it: those with a digit, or four letters or more and not generic. */
function featureAnchors(feature: string) {
  const words = wordsOf(feature)
  const anchors = words.filter(
    (word) =>
      /\d/.test(word) || (word.length >= 4 && !GENERIC_FEATURE_WORDS.has(word))
  )
  if (anchors.length) return anchors
  return words.filter((word) => word.length >= 2)
}

export function chartLookupFor(question: string): ChartLookup | null {
  const match = LOOKUP_QUESTION.exec(question || "")
  if (!match) return null
  const tail = match[1]
    .replace(TRAILING_SOURCE, "")
    .replace(/[\s.?!]+$/, "")
    .trim()
  if (!tail) return null
  const mode: ChartLookup["mode"] =
    /\bor\b/i.test(tail) && !/\band\b/i.test(tail) ? "any" : "all"
  const features = tail
    .split(FEATURE_SPLIT)
    .map((feature) =>
      feature.replace(/^(?:both|either|all\s+of|the|a|an)\s+/i, "").trim()
    )
    .filter((feature) => feature && featureAnchors(feature).length)
  if (!features.length) return null
  return { features, mode }
}

export function isChartLookupQuestion(question: string) {
  return chartLookupFor(question) !== null
}

/** Lines added to the question the reading step receives, so a chart or table is read row by row. */
export function chartReadingGuidance(question: string): string[] {
  if (!chartLookupFor(question)) return []
  return [
    "Read every table or chart row by row. Write one line per row: the row label, then each column heading with that row's value, exactly as shown (Yes, No, a tick, a dash, or the cell text).",
    "Keep the column headings with their values. Do not summarise the table or leave rows out.",
  ]
}

/** Lines added to the question the answering step receives, so an item is listed only when its row shows every feature. */
export function chartLookupGuidance(question: string): string[] {
  const lookup = chartLookupFor(question)
  if (!lookup) return []
  const needed = lookup.mode === "any" ? "at least one of" : "every one of"
  return [
    `Chart or table lookup: list a model only when the row that names it in the document shows ${needed} these: ${lookup.features.join(
      "; "
    )}.`,
    "For each model listed, quote its row, including the cells or words that show each feature.",
    "Do not infer a feature from a model name, a series, or anything outside the document.",
    "If a row shows only some of the features, do not list that model; say which feature its row does not show.",
    "If no row shows the features asked for, say that it was not found.",
  ]
}

export function withChartReadingGuidance(question: string) {
  return [question, ...chartReadingGuidance(question)]
    .filter(Boolean)
    .join("\n")
}

export function withChartLookupGuidance(question: string) {
  return [question, ...chartLookupGuidance(question)].filter(Boolean).join("\n")
}

type ChartRow = {
  label: string
  text: string
  /** Column heading to cell value, for a markdown table row. */
  cells?: { heading: string; value: string }[]
}

const TABLE_SEPARATOR = /^\|?\s*:?-{2,}:?\s*(?:\|\s*:?-{2,}:?\s*)*\|?\s*$/

function tableCells(line: string) {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "")
  return trimmed.split("|").map((cell) => cell.trim())
}

function isTableLine(line: string) {
  return /^\s*\|.*\|?\s*$/.test(line) && (line.match(/\|/g) || []).length >= 2
}

/** The rows of the reading: markdown table rows with their headings, and otherwise lines (long lines split into sentences). */
export function chartRows(extraction: string): ChartRow[] {
  const rows: ChartRow[] = []
  const lines = extraction.replace(/\r\n/g, "\n").split("\n")
  let headings: string[] | null = null
  lines.forEach((raw) => {
    const line = raw.trim()
    if (!line) {
      headings = null
      return
    }
    if (isTableLine(line)) {
      if (TABLE_SEPARATOR.test(line)) return
      const cells = tableCells(line)
      if (!headings) {
        headings = cells
        rows.push({
          label: cells[0] || "",
          text: line,
          cells: cells.map((value) => ({ heading: value, value })),
        })
        return
      }
      const current = headings
      rows.push({
        label: cells[0] || "",
        text: line,
        cells: cells.map((value, index) => ({
          heading: current[index] || "",
          value,
        })),
      })
      return
    }
    headings = null
    const pieces =
      line.length > 160 ? line.split(/(?<=[.;])\s+(?=[A-Z(])/) : [line]
    pieces.forEach((piece) => {
      const text = piece.trim()
      if (!text) return
      const colon = text.indexOf(":")
      const label =
        colon > 0 && colon <= 60 ? text.slice(0, colon).trim() : text
      rows.push({ label, text })
    })
  })
  return rows
}

const NEGATIVE_CELL =
  /^\s*(?:|no|none|n\/a|na|nil|✗|✘|×|x|—|–|-|not\s+available|unavailable|not\s+supported|not\s+included)\s*\.?\s*$/i
const NEGATED_AFTER =
  /^\s*(?:\)|\]|")?\s*[:=]?\s*(?:no\b|none\b|n\/a\b|not\b|✗|✘|×|\bx\b|—|–|-(?=\s|$)|unavailable\b|without\b)/i

function cellMatches(cell: string, anchors: string[]) {
  const hay = compact(cell)
  return anchors.every((anchor) => hay.includes(compact(anchor)))
}

function cellIsAffirmative(value: string) {
  return !NEGATIVE_CELL.test(value)
}

/** True when the feature is shown for the row: in a table cell under the feature's heading, or named in the row text without a negative beside it. */
export function rowShowsFeature(row: ChartRow, feature: string) {
  const anchors = featureAnchors(feature)
  if (row.cells) {
    const column = row.cells.find((cell) => cellMatches(cell.heading, anchors))
    if (column) return cellIsAffirmative(column.value)
  }
  const text = plainDigits(row.text)
  const lower = text.toLowerCase()
  const positions = anchors.map((anchor) => lower.indexOf(anchor))
  if (positions.some((position) => position < 0)) return false
  let cursor = anchors.reduce(
    (end, anchor, index) => Math.max(end, positions[index] + anchor.length),
    0
  )
  // Step over the rest of the feature phrase, so "H2O2 decontamination: –" reads as negated.
  const words = new Set(wordsOf(feature))
  for (;;) {
    const next = /^[\s(),/]*([a-z0-9]+)/.exec(lower.slice(cursor))
    if (!next || !words.has(next[1])) break
    cursor += next[0].length
  }
  return !NEGATED_AFTER.test(text.slice(cursor))
}

/** A table with models as columns: the model's heading column, read against the row labelled with the feature. */
function columnShowsFeature(rows: ChartRow[], item: string, anchors: string[]) {
  return rows.some((row) => {
    if (!row.cells || !cellMatches(row.label, anchors)) return false
    const column = row.cells.find((cell) => containsCode(cell.heading, item))
    return Boolean(column && cellIsAffirmative(column.value))
  })
}

// Up to two capitalised words, then a token with a digit (hyphens allowed), then
// short upper-case words or further digit tokens: "MCO-170AICUVH", "Heracell VIOS 160i".
const CODE_TOKEN = "(?=[A-Za-z0-9-]*\\d)[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*"
const MODEL_CANDIDATE = new RegExp(
  `(?:\\b[A-Z][A-Za-z]*(?:-[A-Za-z]+)*\\s+){0,2}\\b${CODE_TOKEN}(?:\\s+(?:[A-Z]{1,6}\\b|${CODE_TOKEN}))*`,
  "g"
)
const NOT_A_MODEL_PREFIX =
  /^(?:page|pages|figure|fig|table|step|note|section|row|column|iso|iec|en|din|astm|ul|csa|part|version|rev|no|item|chapter|class|type)\b/i
const UNIT_SUFFIX =
  /^\d+(?:[.,]\d+)?\s*(?:s|sec|secs|min|mins|h|hr|hrs|w|kw|l|ml|g|kg|mg|mm|cm|m|c|k|v|a|ma|hz|khz|mhz|rpm|pa|kpa|bar|psi|%|nm|um|µm|ppm|db|lb|lbs|ft|in|x)$/i
const CHEMICAL = /^(?:co2|h2o2|o2|n2|h2o|co|no2|so2|ch4)$/i

/** Model-like names in the reply: runs of words with a digit in them, such as "MCO-170AICUVH" or "Heracell VIOS 160i". */
export function modelCandidates(answer: string, fileName?: string) {
  let text = answer
  if (fileName) {
    const escaped = fileName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    text = text.replace(new RegExp(escaped, "gi"), " ")
  }
  text = text.replace(/\*\*/g, " ").replace(/`/g, " ")
  const found: string[] = []
  const seen = new Set<string>()
  const matches = text.match(MODEL_CANDIDATE) || []
  matches.forEach((raw) => {
    const candidate = raw.trim().replace(/[-\s]+$/, "")
    if (!candidate || !/[A-Za-z]/.test(candidate) || !/\d/.test(candidate))
      return
    if (NOT_A_MODEL_PREFIX.test(candidate)) return
    if (UNIT_SUFFIX.test(candidate)) return
    if (/^\d+-[a-z]+$/.test(candidate)) return
    if (CHEMICAL.test(candidate)) return
    // "Dual-beam IR CO2 sensor" or "165 L": the only digit token is a chemical or a unit.
    const digitTokens = candidate
      .split(/\s+/)
      .filter((token) => /\d/.test(token))
    if (
      digitTokens.every(
        (token) => CHEMICAL.test(token) || UNIT_SUFFIX.test(token)
      )
    )
      return
    if (/^\d{4}$/.test(candidate)) return
    const key = compact(candidate)
    if (!key || seen.has(key)) return
    seen.add(key)
    found.push(candidate)
  })
  return found
}

const NOT_FOUND_REPLY =
  /was not found in the embedded images or the (?:pdf|document) text|no (?:model|row|item)s? (?:in|of) [^.\n]{0,80}(?:shows?|supports?|has|have)|not found in [^.\n]{0,40}(?:chart|table|reading|document)/i

function featureList(features: string[]) {
  return features.join(" and ")
}

export function reviewChartAnswer(input: {
  fileName: string
  question: string
  answer: string
  extraction?: string
}): ChartReview | null {
  const lookup = chartLookupFor(input.question)
  const extraction = (input.extraction || "").trim()
  if (!lookup || !extraction) return null
  const answer = input.answer.trim()
  const { features } = lookup
  const rows = chartRows(extraction)
  const items = modelCandidates(answer, input.fileName)

  if (!items.length) {
    if (NOT_FOUND_REPLY.test(answer)) {
      return {
        grounded: true,
        summary: `No model listed from ${input.fileName} for ${featureList(
          features
        )}. Nothing was inferred.`,
        items: [],
        features,
      }
    }
    return {
      grounded: false,
      summary: `The reply names no model from the chart in ${input.fileName}. ${CHART_ANALYSIS_BEHAVIOR}`,
      items: [],
      features,
    }
  }

  const reviewed: ChartItemReview[] = items.map((item) => {
    if (!containsCode(extraction, item)) {
      return {
        item,
        status: "unverified",
        reason: `Not in the chart reading of ${input.fileName}`,
      }
    }
    const itemRows = rows.filter((row) => containsCode(row.text, item))
    const missing = features.filter((feature) => {
      const anchors = featureAnchors(feature)
      const shown =
        itemRows.some((row) => rowShowsFeature(row, feature)) ||
        columnShowsFeature(rows, item, anchors)
      return !shown
    })
    const shownCount = features.length - missing.length
    const enough = lookup.mode === "any" ? shownCount > 0 : missing.length === 0
    if (enough) {
      const shownFeatures = features.filter(
        (feature) => !missing.includes(feature)
      )
      return {
        item,
        status: "verified",
        reason: `Chart row shows ${featureList(shownFeatures)}`,
      }
    }
    return {
      item,
      status: "unverified",
      reason: `Chart row does not show ${featureList(missing)}`,
    }
  })

  const unverified = reviewed.filter((item) => item.status === "unverified")
  if (!unverified.length) {
    return {
      grounded: true,
      summary: `Verified against ${input.fileName}: ${
        reviewed.length === 1
          ? "the model named has"
          : `all ${reviewed.length} models named have`
      } a chart row showing ${featureList(features)}.`,
      items: reviewed,
      features,
    }
  }
  return {
    grounded: false,
    summary: `${unverified.length} of ${reviewed.length} models named in the reply could not be verified against the chart in ${input.fileName}. ${CHART_ANALYSIS_BEHAVIOR}`,
    items: reviewed,
    features,
  }
}
